import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    IconButton,
    Button,
    Tab,
    Tabs,
    Drawer,
} from "@mui/material";
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import { ArrowBack, Cancel, PlayArrow, ApartmentOutlined, ChevronRight, Check } from "@mui/icons-material";
import Icons from "@/assets";
import NavigationFeedbackModal from "@/components/feedback/NavigationFeedbackModal";
import NotesSection from "@/components/contributionDetails/NotesSection";
import { orderPhotosByView } from "@/utils/helper";
import { getIconForSectionType } from "@/utils/buildingIcons";
import { SectionContribution, NavigationBuilding } from "@/types/common";

// ─── Tab definition ─────────────────────────────────────────────────────────

const TAB_SECTIONS = [
    { key: "entrance", label: "Entrance", icon: Icons.Entrance },
    { key: "exit", label: "Exit", icon: Icons.Exit },
];

// ─── Photo label helpers ─────────────────────────────────────────────────────

const PHOTO_VIEW_KEYWORDS = ["front", "side", "wide"];
const PHOTO_VIEW_LABELS: Record<string, string> = {
    front: "Front View",
    side: "Side View",
    wide: "Wide View",
};

const getPhotoLabel = (url: string, index: number): string => {
    const lower = url.toLowerCase();
    const view = PHOTO_VIEW_KEYWORDS.find((v) => lower.includes(v));
    return view ? PHOTO_VIEW_LABELS[view] : `Photo ${index + 1}`;
};

// ─── Types ───────────────────────────────────────────────────────────────────

type MediaItem =
    | { type: "image"; url: string; label: string }
    | { type: "video"; url: string; label: string };

// ─── Main Component ──────────────────────────────────────────────────────────

const NavigationDetail = () => {
    const navigate = useNavigate();
    const { state } = useLocation();

    const {
        contributionDetails,
        buildings,
        initialBuildingId,
        initialEntryPointId,
        navigation_history_id,
        place_name,
        activeTab: initialTab,
    } = state || {};

    const isMultiBuilding: boolean = Array.isArray(buildings) && buildings.length > 0;

    // ── Legacy (single entrance/exit) mode ─────────────────────────────────────
    const [activeTab, setActiveTab] = useState<"entrance" | "exit">(
        initialTab || "entrance",
    );

    // ── Multi-building mode ─────────────────────────────────────────────────────
    const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(
        initialBuildingId ?? null,
    );
    const [activeEntryPointId, setActiveEntryPointId] = useState<number | null>(
        initialEntryPointId ?? null,
    );
    const [buildingPickerOpen, setBuildingPickerOpen] = useState(false);

    const selectedBuilding: NavigationBuilding | null = isMultiBuilding
        ? (buildings as NavigationBuilding[]).find(
              (b) => b.building_id === selectedBuildingId,
          ) ?? null
        : null;

    const activeEntryPoint = selectedBuilding?.entryPoints.find(
        (ep) => ep.entry_point_id === activeEntryPointId,
    ) ?? null;

    const handleBuildingSelect = (building: NavigationBuilding) => {
        setSelectedBuildingId(building.building_id);
        setActiveEntryPointId(building.entryPoints[0]?.entry_point_id ?? null);
        setBuildingPickerOpen(false);
    };

    // ── Feedback modal state ───────────────────────────────────────────────────
    const [feedbackOpen, setFeedbackOpen] = useState(false);

    // ── Build media list for active tab/entry point ────────────────────────────
    const sectionData: SectionContribution | null = isMultiBuilding
        ? activeEntryPoint
        : contributionDetails?.[activeTab] ?? null;

    const photos = orderPhotosByView(
        sectionData?.photos ?? [],
        PHOTO_VIEW_KEYWORDS,
    ).filter((p): p is { url: string } => p !== null);
    const videoUrl = sectionData?.video ?? null;

    const mediaList: MediaItem[] = [
        ...photos.map((p, i) => ({
            type: "image" as const,
            url: p.url,
            label: getPhotoLabel(p.url, i),
        })),
        ...(videoUrl
            ? [{ type: "video" as const, url: videoUrl, label: "Video" }]
            : []),
    ];

    // ── Active hero item ───────────────────────────────────────────────────────
    const [heroIndex, setHeroIndex] = useState(0);

    // Reset hero when tab/entry point changes
    useEffect(() => {
        setHeroIndex(0);
    }, [activeTab, activeEntryPointId]);

    const heroItem = mediaList[heroIndex] ?? null;
    const activeSectionLabel = isMultiBuilding
        ? activeEntryPoint?.entry_name ?? "this section"
        : TAB_SECTIONS.find((s) => s.key === activeTab)!.label;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
                backgroundColor: "#fff",
                overflow: "hidden",
            }}
        >
            {/* ── Hero media preview (top ~45% of screen) ──────────────────────── */}
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: "50vh",
                    flexShrink: 0,
                    backgroundColor: "#111",
                    overflow: "hidden",
                }}
            >
                {heroItem ? (
                    heroItem.type === "image" ? (
                        <Box
                            component="img"
                            src={heroItem.url}
                            alt={heroItem.label}
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                    ) : (
                        <Box
                            component="video"
                            src={heroItem.url}
                            controls
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                backgroundColor: "#000",
                            }}
                        />
                    )
                ) : (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Typography sx={{ color: "#666", fontSize: 13 }}>
                            No media
                        </Typography>
                    </Box>
                )}
                {place_name && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 25,
                            left: "50%",
                            transform: "translateX(-50%)",
                            backgroundColor: "rgba(0,0,0,0.55)",
                            borderRadius: "16px",
                            px: "10px",
                            py: "5px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "5px",
                            maxWidth: "70%",
                            overflow: "hidden",
                        }}
                    >
                        <FmdGoodOutlinedIcon sx={{ fontSize: 20, color: "#fff", flexShrink: 0 }} />
                        <Typography
                            sx={{
                                color: "#fff",
                                fontSize: { xs: "3vw", sm: 12 },
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {place_name}
                        </Typography>
                    </Box>
                )}

                {/* Label badge */}
                {heroItem && (
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 25,
                            left: "50%",
                            transform: "translateX(-50%)",
                            backgroundColor: "rgba(0,0,0,0.55)",
                            borderRadius: "8px",
                            px: "10px",
                            py: "4px",
                        }}
                    >
                        <Typography sx={{ color: "#fff", fontSize: { xs: "3vw", sm: 12 }, fontWeight: 600 }}>
                            {heroItem.label}
                        </Typography>
                    </Box>
                )}

                {/* Back arrow overlaid */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 25,
                        left: 10,
                    }}
                >
                    <IconButton
                        onClick={() => navigate(-1)}
                        size="small"
                        sx={{
                            backgroundColor: "rgba(0,0,0,0.45)",
                            color: "#fff",
                            "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
                        }}
                    >
                        <ArrowBack sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
            </Box>

            {/* ── Building switcher (multi-building mode only) ─────────────────── */}
            {isMultiBuilding && (
                <Box
                    onClick={() => setBuildingPickerOpen(true)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        px: "4%",
                        py: "2%",
                        backgroundColor: "#F8F4FF",
                        borderBottom: "1px solid #F0F0F0",
                        cursor: "pointer",
                    }}
                >
                    <ApartmentOutlined sx={{ color: "#6f2fa8", fontSize: 18 }} />
                    <Typography
                        sx={{
                            flex: 1,
                            fontSize: { xs: "3.4vw", sm: 14 },
                            fontWeight: 700,
                            color: "#191919",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {selectedBuilding?.building_name ?? "Select building"}
                    </Typography>
                    <ChevronRight sx={{ color: "#6f2fa8", fontSize: 18 }} />
                </Box>
            )}

            {/* ── Tabs ─────────────────────────────────────────────────────────── */}
            <Box sx={{ borderBottom: "1px solid #F0F0F0", backgroundColor: "#fff" }}>
                {isMultiBuilding ? (
                    <Tabs
                        value={activeEntryPointId ?? false}
                        onChange={(_e, val) => setActiveEntryPointId(val as number)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            px: "4%",
                            minHeight: "44px",
                            "& .MuiTabs-indicator": {
                                backgroundColor: "#9443DF",
                                height: "2.5px",
                                borderRadius: "2px",
                            },
                        }}
                    >
                        {(selectedBuilding?.entryPoints ?? []).map((ep) => (
                            <Tab
                                key={ep.entry_point_id}
                                value={ep.entry_point_id}
                                label={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                        {getIconForSectionType(ep.section_type ?? ep.entry_name, "18px")}
                                        <Typography
                                            sx={{
                                                fontSize: { xs: "3.4vw", sm: 14 },
                                                fontWeight: activeEntryPointId === ep.entry_point_id ? 700 : 500,
                                                color: activeEntryPointId === ep.entry_point_id ? "#9443DF" : "#666",
                                                textTransform: "none",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {ep.entry_name}
                                        </Typography>
                                    </Box>
                                }
                                sx={{ minHeight: "44px", px: "4%", py: 0 }}
                            />
                        ))}
                    </Tabs>
                ) : (
                    <Tabs
                        value={activeTab}
                        onChange={(_e, val) => setActiveTab(val)}
                        sx={{
                            px: "4%",
                            minHeight: "44px",
                            "& .MuiTabs-indicator": {
                                backgroundColor: "#9443DF",
                                height: "2.5px",
                                borderRadius: "2px",
                            },
                        }}
                    >
                        {TAB_SECTIONS.map((section) => (
                            <Tab
                                key={section.key}
                                value={section.key}
                                label={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                        <Box
                                            component="img"
                                            src={section.icon}
                                            alt={section.label}
                                            sx={{ width: 20, height: 20, objectFit: "contain" }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: { xs: "3.4vw", sm: 14 },
                                                fontWeight: activeTab === section.key ? 700 : 500,
                                                color: activeTab === section.key ? "#9443DF" : "#666",
                                                textTransform: "none",
                                            }}
                                        >
                                            {section.label}
                                        </Typography>
                                    </Box>
                                }
                                sx={{ minHeight: "44px", px: "4%", py: 0 }}
                            />
                        ))}
                    </Tabs>
                )}
            </Box>

            {/* ── Scrollable content ────────────────────────────────────────────── */}
            <Box sx={{ flex: 1, overflowY: "auto", pb: "24%" }}>

                {/* Photos & Videos thumbnail strip */}
                {mediaList.length > 0 && (
                    <Box sx={{ px: "4%", pt: "4%", pb: "2%" }}>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: "3.4vw", sm: 14 },
                                color: "#191919",
                                mb: "3%",
                            }}
                        >
                            Photos &amp; Videos
                        </Typography>

                        <Box sx={{ display: "flex", flexDirection: "row", gap: "3%", flexWrap: "wrap" }}>
                            {mediaList.map((item, i) => {
                                const isActive = i === heroIndex;
                                return (
                                    <Box
                                        key={item.url + i}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: "5px",
                                            cursor: "pointer",
                                        }}
                                        onClick={() => setHeroIndex(i)}
                                    >
                                        <Box
                                            sx={{
                                                position: "relative",
                                                width: { xs: "20vw", sm: 80 },
                                                height: { xs: "20vw", sm: 80 },
                                                borderRadius: "10px",
                                                overflow: "hidden",
                                                border: isActive
                                                    ? "2.5px solid #9443DF"
                                                    : "1.5px solid #E0E0E0",
                                                transition: "border-color 0.2s",
                                                backgroundColor: "#000",
                                            }}
                                        >
                                            {item.type === "image" ? (
                                                <Box
                                                    component="img"
                                                    src={item.url}
                                                    alt={item.label}
                                                    sx={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                    }}
                                                />
                                            ) : (
                                                <>
                                                    <Box
                                                        component="video"
                                                        src={item.url}
                                                        muted
                                                        preload="metadata"
                                                        sx={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                            pointerEvents: "none",
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            inset: 0,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            backgroundColor: "rgba(0,0,0,0.28)",
                                                        }}
                                                    >
                                                        <PlayArrow sx={{ color: "#fff", fontSize: { xs: "6vw", sm: 24 } }} />
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: "2.8vw", sm: 11 },
                                                color: isActive ? "#9443DF" : "#555",
                                                fontWeight: isActive ? 700 : 500,
                                                transition: "color 0.2s",
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}

                {/* Notes */}
                {sectionData?.notes && (
                    <Box sx={{ px: "4%", pt: "2%", pb: "2%" }}>
                        <NotesSection data={[sectionData.notes]} noHeaderText optionTick />
                    </Box>
                )}

                {/* Empty state */}
                {!mediaList.length && !sectionData?.notes && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "30vh",
                        }}
                    >
                        <Typography sx={{ color: "#999", fontSize: { xs: "3.4vw", sm: 14 } }}>
                            No data available for {activeSectionLabel}.
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* ── End Navigation Button ────────────────────────────────────────── */}
            <Box
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    px: "5%",
                    py: "2%",
                    backgroundColor: "#fff",
                    borderTop: "1px solid #F0F0F0",
                    boxShadow: "0px -2px 12px rgba(0,0,0,0.06)",
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    onClick={() => setFeedbackOpen(true)}
                    startIcon={<Cancel />}
                    sx={{
                        borderRadius: "30px",
                        py: "3%",
                        fontSize: { xs: "3.88vw", sm: 15 },
                        fontWeight: 700,
                        background: "linear-gradient(135deg, #E8D5FF 0%, #D4ABFF 100%)",
                        color: "#6B21A8",
                        boxShadow: "none",
                        textTransform: "none",
                        "& .MuiButton-startIcon svg": { color: "#9443DF" },
                        "&:hover": {
                            background: "linear-gradient(135deg, #DEC4FF 0%, #C084FC 100%)",
                            boxShadow: "none",
                        },
                    }}
                >
                    End Navigation
                </Button>
            </Box>

            {/* ── Navigation Feedback Modal ─────────────────────────────────── */}
            <NavigationFeedbackModal
                open={feedbackOpen}
                onClose={() => setFeedbackOpen(false)}
                navigationHistoryId={navigation_history_id}
            />

            {/* ── Building Picker Drawer (multi-building mode only) ────────────── */}
            {isMultiBuilding && (
                <Drawer
                    anchor="bottom"
                    open={buildingPickerOpen}
                    onClose={() => setBuildingPickerOpen(false)}
                    PaperProps={{
                        sx: {
                            borderTopLeftRadius: "20px",
                            borderTopRightRadius: "20px",
                            maxHeight: "70vh",
                        },
                    }}
                >
                    <Box sx={{ px: "5%", pt: "4%", pb: "6%" }}>
                        <Typography sx={{ fontSize: "4.2vw", fontWeight: 700, color: "#191919", mb: "4%" }}>
                            Select Building
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "3%" }}>
                            {(buildings as NavigationBuilding[]).map((building) => {
                                const isSelected = building.building_id === selectedBuildingId;
                                return (
                                    <Box
                                        key={building.building_id}
                                        onClick={() => handleBuildingSelect(building)}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "3%",
                                            p: "3.5%",
                                            borderRadius: "12px",
                                            backgroundColor: isSelected ? "#F0DEFF" : "#F8F8F8",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <ApartmentOutlined sx={{ color: "#6f2fa8" }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: "3.6vw", color: "#191919" }}>
                                                {building.building_name}
                                            </Typography>
                                            <Typography sx={{ fontSize: "3vw", color: "#666" }}>
                                                {building.entryPoints.length} entry point{building.entryPoints.length !== 1 ? "s" : ""}
                                            </Typography>
                                        </Box>
                                        {isSelected && <Check sx={{ color: "#6f2fa8" }} />}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Drawer>
            )}
        </Box>
    );
};

export default NavigationDetail;
